// 导入必要的类型和工具
import { StakeBill } from "@/interface";
import useConnectWallet from "@/store/wallet/useConnectWallet";
import { formatUnits } from "ethers";
import { useCallback, useEffect, useState } from "react";

// 每次获取数据的条数限制
const limit = 20;

/**
 * useGetStakeByLimit - 分页获取质押账单数据
 *
 * 用于调用 RegularStaking 合约的 getStakeByLimit 方法分页获取用户质押账单。
 * 支持自动加载和滚动加载更多数据。
 */
const useGetStakeByLimit = () => {
  // 获取钱包地址和合约实例
  const {
    contractEvm: { RegularStaking },
    address,
  } = useConnectWallet();
  // 定义状态变量
  const [data, setData] = useState<StakeBill[]>([]); // 质押账单数据
  const [getStakeByLimitLod, setLoading] = useState(false); // 加载状态
  const [start, setStart] = useState("1000000000000000000"); // 分页起始点
  const [end, setEnd] = useState(false); // 分页结束点
  const [ready, setReady] = useState(false); // 标记状态设置完毕

  // 获取质押账单数据的函数
  const getStakeByLimit = useCallback(async () => {
    if (RegularStaking) {
      try {
        setLoading(true);
        // 调用合约方法获取数据
        const res = await RegularStaking.getStakeByLimit(address, start, limit);
        setEnd(res.length < limit);
        // 如果返回数据不为空，更新分页起始点
        if (res.length) {
          const id = Number(formatUnits(res[res.length - 1].id, 0)) - 1 || 0;
          if (id <= 0) {
            setStart("0");
          } else {
            setStart(id.toString());
          }
          // 将新数据追加到质押账单数据中
          setData([...data, ...res]);
        } else {
          setStart("0");
        }
      } catch (error) {
        console.log("useGetStakeByLimit", error);
      } finally {
        setLoading(false);
      }
    }
  }, [RegularStaking, address, data, start]);

  // 第一步：初始化状态
  useEffect(() => {
    if (RegularStaking) {
      setData([]);
      setStart("1000000000000000000");
      setEnd(false);
      setReady(true);
    }
  }, [RegularStaking]);

  // 第二步：当状态设置完后执行请求
  useEffect(() => {
    if (ready) {
      getStakeByLimit();
      setReady(false);
    }
  }, [getStakeByLimit, ready]);

  // 返回 Hook 的方法和状态
  return { getStakeByLimit, data, getStakeByLimitLod, end };
};

// 导出自定义 Hook
export default useGetStakeByLimit;
